import { useMemo } from "react";
import { useCachedQuery } from "./useCachedQuery";

export type AuditAction =
  | "USER_PROFILE_UPDATED"
  | "CLAW_CREATED"
  | "CLAW_DELETED"
  | "CLAW_REASSIGNED"
  | "CLAW_UNLOCKED"
  | "CLAW_LLM_TOKEN_SET"
  | "CLAW_GATEWAY_PASSWORD_SET"
  | "CLAW_CONTROL_UI_IDENTITY_SET"
  | "CLAW_AI_MODE_SWITCHED";

export type AuditEvent = {
  eventTime: string;
  action: AuditAction;
  tenantId: string;
  tenantName: string;
  tier?: string;
  instanceId?: string;
  actorEmail?: string;
  // Who the action was done TO (reassign target, secure-note owner, ...).
  targetEmail?: string;
};

export type AuditStatus = "loading" | "ready" | "error";

export type UseAuditEventsResult = {
  status: AuditStatus;
  events: ReadonlyArray<AuditEvent>;
  error: string | null;
  reload: () => Promise<void>;
};

export type AuditScope = "self" | "admin";

// A bare scope reads the platform-wide / self stream (cached). The tenant
// variant scopes to one instance over a time window for the per-instance
// dashboard panel — windowed + per-tenant, so it's network-only (no cache).
export type AuditQuery = AuditScope | { scope: "tenant"; tenantId: string; hours: number };

const ENDPOINT_BY_SCOPE: Record<AuditScope, string> = {
  self: "/api/audit/events",
  admin: "/api/admin/audit/events",
};

const CACHE_RESOURCE_BY_SCOPE: Record<AuditScope, string> = {
  self: "audit:self",
  admin: "admin:audit",
};

const PERSIST_CAP = 100;

type ResolvedQuery = {
  endpoint: string;
  cacheResource: string | null;
};

const resolveQuery = (query: AuditQuery): ResolvedQuery => {
  if (typeof query === "string") {
    return { endpoint: ENDPOINT_BY_SCOPE[query], cacheResource: CACHE_RESOURCE_BY_SCOPE[query] };
  }
  const params = new URLSearchParams();
  params.set("tenantId", query.tenantId);
  params.set("hours", String(query.hours));
  return {
    endpoint: `/api/admin/audit/events?${params.toString()}`,
    cacheResource: null,
  };
};

export const useAuditEvents = (query: AuditQuery = "self"): UseAuditEventsResult => {
  const { endpoint, cacheResource } = resolveQuery(query);

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<ReadonlyArray<AuditEvent>> => {
      const res = await fetch(endpoint, { credentials: "include", signal });
      if (!res.ok) throw new Error(`Failed to load audit events (${res.status})`);
      return (await res.json()) as AuditEvent[];
    },
    [endpoint],
  );

  const { status, data, error, refetch } = useCachedQuery<ReadonlyArray<AuditEvent>>({
    cacheResource,
    queryKey: endpoint,
    fetcher,
    persistTransform: (events) => events.slice(0, PERSIST_CAP),
  });

  return { status, events: data ?? [], error, reload: refetch };
};
